import React, { useEffect, useState } from "react";
import { Header } from "../../components/Header/Header";
import { Navigator } from "../../components/Navigator/Navigator";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getAllCustomers, adminResetFingerprint } from "../../context/Auth";
import Swal from "sweetalert2";

function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/login");
      return;
    }
    fetchCustomers();
  }, [user]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getAllCustomers();
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error("Error al obtener clientes:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter((c) => {
    const matchesSearch =
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === "all" || c.customer_type === filterType;
    const matchesStatus = filterStatus === "all" || c.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-700",
      inactive: "bg-gray-100 text-gray-600",
      canceled: "bg-red-100 text-red-700",
    };
    const labels = {
      active: "Activo",
      inactive: "Inactivo",
      canceled: "Cancelado",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-[11px] font-medium ${
          styles[status] || "bg-gray-100 text-gray-600"
        }`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const getSubStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-700",
      canceled: "bg-red-100 text-red-700",
      expired: "bg-orange-100 text-orange-700",
      pending: "bg-yellow-100 text-yellow-700",
    };
    const labels = {
      active: "Activa",
      canceled: "Cancelada",
      expired: "Expirada",
      pending: "Pendiente",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-[11px] font-medium ${
          styles[status] || "bg-gray-100 text-gray-600"
        }`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const isInmo = type === "inmobiliaria";
    return (
      <span
        className={`px-2 py-1 rounded-full text-[11px] font-medium ${
          isInmo
            ? "bg-blue-100 text-blue-700"
            : "bg-purple-100 text-purple-700"
        }`}
      >
        {isInmo ? "Inmobiliaria" : "Inversionista"}
      </span>
    );
  };

  const formatPlan = (plan) => {
    if (!plan) return "—";
    const planLabels = {
      inversionista_monthly: "Inversionista Mensual",
      inversionista_yearly: "Inversionista Anual",
      inmobiliaria_monthly: "Inmobiliaria Mensual",
      inmobiliaria_yearly: "Inmobiliaria Anual",
    };
    return planLabels[plan] || plan;
  };

  const formatCurrency = (amount, currency = "mxn") => {
    if (!amount) return "—";
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const getSubscriptionCost = (customer) => {
    const sub = customer.subscriptions?.[0];
    const payment = sub?.payments?.[0];
    if (payment) return formatCurrency(payment.amount, payment.currency);
    return "—";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const toggleExpand = (id) => {
    setExpandedCustomer(expandedCustomer === id ? null : id);
  };

  const handleResetFingerprint = async (customerId, name) => {
    const result = await Swal.fire({
      title: "Resetear dispositivo",
      text: `Se desvinculara el dispositivo de "${name || "este cliente"}". Podra iniciar sesion desde un nuevo dispositivo.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4C63ED",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, resetear",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await adminResetFingerprint(customerId);
      Swal.fire({
        icon: "success",
        title: "Dispositivo desvinculado",
        showConfirmButton: false,
        timer: 1500,
      });
      fetchCustomers();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo resetear el dispositivo",
      });
    }
  };

  return (
    <div>
      <main className="w-[100vw] max-w-[100vw] bg-[#eaeaea]">
        <Header />
        <div className="flex h-full w-full pr-[20px] pb-[20px]">
          <Navigator />
          <div className="flex flex-col w-full max-h-[calc(100svh-120px)] overflow-y-auto bg-white rounded-[4px]">
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-gray-200">
              <h1 className="text-[24px] font-[500] mb-4">Clientes</h1>
              <div className="flex flex-wrap gap-3 items-center">
                <input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 min-w-[200px] pl-3 pr-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-[#4C63ED]"
                />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-[#4C63ED]"
                >
                  <option value="all">Todos los tipos</option>
                  <option value="inmobiliaria">Inmobiliaria</option>
                  <option value="inversionista">Inversionista</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border rounded-[6px] text-[14px] focus:outline-none focus:ring-1 focus:ring-[#4C63ED]"
                >
                  <option value="all">Todos los estados</option>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="canceled">Cancelado</option>
                </select>
                <span className="text-[13px] text-gray-500">
                  {filteredCustomers.length} cliente
                  {filteredCustomers.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>

            {/* Table */}
            <div className="px-6 py-4">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4C63ED]"></div>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                  No se encontraron clientes
                </div>
              ) : (
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="pb-3 font-medium">Cliente</th>
                      <th className="pb-3 font-medium">Tipo</th>
                      <th className="pb-3 font-medium">Plan</th>
                      <th className="pb-3 font-medium">Costo</th>
                      <th className="pb-3 font-medium">Estado</th>
                      <th className="pb-3 font-medium">Suscripcion</th>
                      <th className="pb-3 font-medium">Vencimiento</th>
                      <th className="pb-3 font-medium">Fingerprint</th>
                      <th className="pb-3 font-medium">Equipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((customer) => (
                      <React.Fragment key={customer.id}>
                        <tr
                          className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                            customer.customer_type === "inmobiliaria" &&
                            customer.teamMembers?.length > 0
                              ? "cursor-pointer"
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              customer.customer_type === "inmobiliaria" &&
                              customer.teamMembers?.length > 0
                            ) {
                              toggleExpand(customer.id);
                            }
                          }}
                        >
                          <td className="py-3">
                            <div>
                              <p className="font-medium text-[14px]">
                                {customer.name || "Sin nombre"}
                              </p>
                              <p className="text-gray-400 text-[12px]">
                                {customer.email}
                              </p>
                            </div>
                          </td>
                          <td className="py-3">{getTypeBadge(customer.customer_type)}</td>
                          <td className="py-3">
                            <span className="text-[13px]">{formatPlan(customer.plan)}</span>
                          </td>
                          <td className="py-3">
                            <span className="text-[13px] font-medium">{getSubscriptionCost(customer)}</span>
                          </td>
                          <td className="py-3">{getStatusBadge(customer.status)}</td>
                          <td className="py-3">
                            {customer.subscriptions?.length > 0
                              ? getSubStatusBadge(customer.subscriptions[0].status)
                              : <span className="text-gray-400">Sin suscripcion</span>}
                          </td>
                          <td className="py-3">
                            {customer.subscriptions?.length > 0
                              ? formatDate(customer.subscriptions[0].endDate)
                              : "—"}
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[12px] font-mono ${
                                  customer.fingerprint
                                    ? "text-gray-600"
                                    : "text-gray-300"
                                }`}
                              >
                                {customer.fingerprint
                                  ? customer.fingerprint.substring(0, 12) + "..."
                                  : "No vinculado"}
                              </span>
                              {customer.fingerprint && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResetFingerprint(customer.id, customer.name);
                                  }}
                                  className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                  title="Resetear dispositivo"
                                >
                                  Resetear
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-3">
                            {customer.customer_type === "inmobiliaria" ? (
                              <span className="flex items-center gap-1">
                                <span className="text-[13px]">
                                  {customer.teamMembers?.length || 0}/
                                  {customer.quantity - 1}
                                </span>
                                {customer.teamMembers?.length > 0 && (
                                  <svg
                                    className={`w-4 h-4 transition-transform ${
                                      expandedCustomer === customer.id
                                        ? "rotate-180"
                                        : ""
                                    }`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-[12px]">N/A</span>
                            )}
                          </td>
                        </tr>
                        {/* Subcustomers expandible */}
                        {expandedCustomer === customer.id &&
                          customer.teamMembers?.map((member) => (
                            <tr
                              key={member.id}
                              className="bg-gray-50 border-b border-gray-100"
                            >
                              <td className="py-2 pl-8">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-300">└</span>
                                  <div>
                                    <p className="font-medium text-[13px]">
                                      {member.name || "Sin nombre"}
                                    </p>
                                    <p className="text-gray-400 text-[11px]">
                                      {member.email}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-2">
                                <span className="text-[11px] text-gray-400">
                                  Miembro
                                </span>
                              </td>
                              <td className="py-2">
                                <span className="text-[12px] text-gray-400">
                                  {formatPlan(member.plan) || "—"}
                                </span>
                              </td>
                              <td className="py-2">
                                <span className="text-gray-300 text-[12px]">—</span>
                              </td>
                              <td className="py-2">
                                {getStatusBadge(member.status)}
                              </td>
                              <td className="py-2">
                                <span className="text-gray-300 text-[12px]">
                                  Via propietario
                                </span>
                              </td>
                              <td className="py-2">
                                <span className="text-gray-300 text-[12px]">—</span>
                              </td>
                              <td className="py-2">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-[12px] font-mono ${
                                      member.fingerprint
                                        ? "text-gray-600"
                                        : "text-gray-300"
                                    }`}
                                  >
                                    {member.fingerprint
                                      ? member.fingerprint.substring(0, 12) + "..."
                                      : "No vinculado"}
                                  </span>
                                  {member.fingerprint && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResetFingerprint(member.id, member.name);
                                      }}
                                      className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                      title="Resetear dispositivo"
                                    >
                                      Resetear
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="py-2"></td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export { Customers };
